import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../providers/AuthProvider';
import { ApiService } from '../services/api';
import toast from 'react-hot-toast';
import LoginBg from '../assets/images/login_bg.jpg';
import LogoString from '../assets/images/logo_string.svg';

const registerSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string(),
  companyName: z.string().min(2, 'Company name must be at least 2 characters'),
  companyAddress: z.string().optional(),
  companyPhone: z.string().min(5, 'Phone number must be at least 5 characters'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type RegisterForm = z.infer<typeof registerSchema>;

const Register: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { login } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
  });

  const onSubmit = async (data: RegisterForm) => {
    setIsLoading(true);
    setError('');

    try {
      const result = await ApiService.register({
        email: data.email,
        password: data.password,
        companyName: data.companyName,
        companyAddress: data.companyAddress || '',
        companyPhone: data.companyPhone,
      });
      
      // Use the auth context to login
      login(result.user, result.token);

      toast.success('Registration successful! Welcome to Loop Settlement Platform.');
      
      // Redirect to dashboard
      navigate('/dashboard');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Registration failed';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-black relative overflow-hidden py-4 sm:py-8">
      {/* Background image */}
      <img src={LoginBg} alt="Register Background" className="absolute inset-0 w-full h-full object-cover object-bottom" />

      <div className="flex flex-col lg:flex-row items-center justify-center w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 gap-8 lg:gap-0">
        {/* Logo Section */}
        <div className="flex-1 flex justify-center lg:justify-start w-full lg:w-auto">
          <img src={LogoString} alt="Logo" className="w-48 sm:w-64 md:w-80 lg:w-96 h-auto" />
        </div>

        {/* Register Form */}
        <div className="flex-1 flex justify-center lg:justify-end w-full lg:w-auto">
          <div className="w-full max-w-md">
            <div className="bg-[#182233] rounded-2xl p-6 sm:p-8 shadow-2xl max-h-[85vh] sm:max-h-[80vh] overflow-y-auto">
              <h2 className="text-xl sm:text-2xl font-bold text-white text-center mb-2">
                Create your account
              </h2>
              <p className="text-center text-xs sm:text-sm text-gray-400 mb-6 sm:mb-8">
                Or{' '}
                <Link
                  to="/login"
                  className="font-medium text-[#2777EF] hover:text-[#0053E2]"
                >
                  sign in to your existing account
                </Link>
              </p>
              
              <form className="space-y-3 sm:space-y-4" onSubmit={handleSubmit(onSubmit)}>
                {error && (
                  <div className="bg-red-900/20 border border-red-500/50 text-red-300 px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}
                
                <div className="space-y-3 sm:space-y-4">
                  <div>
                    <div className="relative">
                      <input
                        {...register('email')}
                        type="email"
                        autoComplete="email"
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-[#567095]/20 border-0 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2777EF] text-sm sm:text-base"
                        placeholder="Email"
                      />
                    </div>
                    {errors.email && (
                      <p className="mt-1 text-xs sm:text-sm text-red-400">{errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <div className="relative">
                      <input
                        {...register('password')}
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-[#567095]/20 border-0 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2777EF] pr-10 sm:pr-12 text-sm sm:text-base"
                        placeholder="Password"
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        <button
                          type="button"
                          className="text-gray-400 hover:text-gray-300"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" />
                          ) : (
                            <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
                          )}
                        </button>
                      </div>
                    </div>
                    {errors.password && (
                      <p className="mt-1 text-xs sm:text-sm text-red-400">{errors.password.message}</p>
                    )}
                  </div>

                  <div>
                    <div className="relative">
                      <input
                        {...register('confirmPassword')}
                        type={showConfirmPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-[#567095]/20 border-0 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2777EF] pr-10 sm:pr-12 text-sm sm:text-base"
                        placeholder="Confirm Password"
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        <button
                          type="button"
                          className="text-gray-400 hover:text-gray-300"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" />
                          ) : (
                            <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
                          )}
                        </button>
                      </div>
                    </div>
                    {errors.confirmPassword && (
                      <p className="mt-1 text-xs sm:text-sm text-red-400">{errors.confirmPassword.message}</p>
                    )}
                  </div>

                  <div>
                    <div className="relative">
                      <input
                        {...register('companyName')}
                        type="text"
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-[#567095]/20 border-0 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2777EF] text-sm sm:text-base"
                        placeholder="Company Name"
                      />
                    </div>
                    {errors.companyName && (
                      <p className="mt-1 text-xs sm:text-sm text-red-400">{errors.companyName.message}</p>
                    )}
                  </div>

                  <div>
                    <div className="relative">
                      <textarea
                        {...register('companyAddress')}
                        rows={3}
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-[#567095]/20 border-0 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2777EF] resize-none text-sm sm:text-base"
                        placeholder="Company Address"
                      />
                    </div>
                    {errors.companyAddress && (
                      <p className="mt-1 text-xs sm:text-sm text-red-400">{errors.companyAddress.message}</p>
                    )}
                  </div>

                  <div>
                    <div className="relative">
                      <input
                        {...register('companyPhone')}
                        type="tel"
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-[#567095]/20 border-0 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2777EF] text-sm sm:text-base"
                        placeholder="Company Phone"
                      />
                    </div>
                    {errors.companyPhone && (
                      <p className="mt-1 text-xs sm:text-sm text-red-400">{errors.companyPhone.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-2.5 sm:py-3 px-4 bg-[#2777EF] hover:bg-[#0053E2] text-white font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm sm:text-base"
                  >
                    {isLoading ? 'Creating account...' : 'Create account'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="absolute bottom-2 sm:bottom-4 left-1/2 transform -translate-x-1/2 text-white text-xs sm:text-sm z-10">
        © 2025 LoopChain
      </div>
    </div>
  );
};

export default Register;
